import React from 'react';
import ReactDOM from 'react-dom/client';
import { App } from './App';
import { AuthProvider } from './contexts/AuthContext';
import { useUser } from './hooks/useUser';
import { AthleteContextProvider } from './hooks/useAthleteContext';
import { NotificationsProvider } from './hooks/useNotifications';
import { ErrorBoundary } from './components/ErrorBoundary';

// Styles load order matters: reset → tokens (defines :root custom properties)
// → global (uses tokens, may override reset). Component-level .module.css
// loads through its own component imports, after these globals.
import './styles/reset.css';
import './styles/tokens.css';
import './styles/global.css';

// Provider order:
//   ErrorBoundary (outermost) — catches everything including AuthProvider init
//     AuthProvider — fetches /api/config, exposes Supabase client + session
//       AthleteContextProvider — single source of truth for athlete_context
//                                state across all routes (Phase 6b1.5 PR 2)
//         NotificationsProvider — single notifications fetch + 60s poll
//                                 shared by every bell/chip (UX S2 F1 fix;
//                                 reads the athlete-context pref map so it
//                                 mounts inside AthleteContextProvider)
//           App — routes consume auth + athlete-context via hooks
//
// AuthProvider does NOT gate render; existing routes (today, onboarding,
// landing, primitives) ignore it cleanly. Phase 4b PR 4 (chat shell)
// is the first auth consumer.
//
// B180 (2026-09-22) — the athlete tree is KEYED ON THE UID (see
// `AthleteTree` below). Without a key, an in-place account switch (uid A
// -> uid B, no sign-out) updates AthleteContextProvider's internal state
// in place; the single commit before its effects run can paint B's uid
// against A's still-resolved role state (round-46/round-52 security,
// B180). Keying AthleteContextProvider on the uid makes React discard
// and remount the whole subtree on a switch instead of patching it, so
// every provider below the key (AthleteContextProvider itself,
// NotificationsProvider, and the routed App underneath) starts over
// clean for the new uid. AuthProvider stays OUTSIDE the key — it is the
// uid source, and remounting it would drop the very session that
// produced the new uid.
//
// AthleteContextProvider (added 2026-05-10) replaces the previous
// per-call useState pattern in `useAthleteContext`. Each call to
// the hook used to spawn an independent state instance — that
// broke cross-component synchronization when, e.g., a Settings
// modal saved through `useSettingsPersist` (which holds its OWN
// instance) but the route-level instance held by `settings.tsx`
// stayed stale until a manual page refresh. The Provider ensures
// every consumer shares the same state.
// Mobile arc Part B — the Capacitor shell renders edge-to-edge
// (viewport-fit=cover), so the app opts into global safe-area padding
// via a root class the stylesheet keys on. Web never gets the class.
import { isNativeApp } from './lib/platform';
import {
  hideKeyboardAccessoryBar,
  initNativeKeyboardLayout,
} from './lib/keyboard/nativeKeyboard';
if (isNativeApp()) {
  document.documentElement.classList.add('native-app');
  // Native-only (founder-approved decision A, 2026-07-21): hide iOS's form-
  // accessory bar (the up/down/Done strip above the keyboard). Fire-and-forget,
  // no-throw.
  void hideKeyboardAccessoryBar();
  // Native-only (B-31c, 2026-07-21): wire the LIVE keyboard height into
  // `--tayro-kb-h` + toggle html.chat-kb-open from the plugin's real
  // keyboardWillShow/Hide events, so the chat frame sizes deterministically to
  // the top of the keyboard. Necessary because installing @capacitor/keyboard
  // disables WKWebView's native auto-pan even under resize:'none' (which still
  // holds — the view does not resize). Fire-and-forget; the app lives for the
  // process lifetime, so the teardown is intentionally not retained.
  void initNativeKeyboardLayout();
}

/**
 * B180 — keys `AthleteContextProvider` (and everything nested inside it)
 * on the signed-in uid, so an in-place account switch remounts the tree
 * instead of patching it. Any uid-to-uid, null-to-uid or uid-to-null
 * transition now remounts too (the ordinary first sign-in and sign-out
 * paths, not only a switch): harmless, because `AthleteContextProvider`'s
 * own effects already no-op while `userId` is null (see `useAthleteContext`),
 * so the instance discarded on those transitions never did any work.
 *
 * A small wrapper because the uid isn't available at this module's top
 * level — it lives on `AuthContext`, one layer in.
 */
export function AthleteTree({ children }: { children: React.ReactNode }): JSX.Element {
  const { user } = useUser();
  return (
    <AthleteContextProvider key={user?.id}>
      <NotificationsProvider>{children}</NotificationsProvider>
    </AthleteContextProvider>
  );
}

// Guarded (rather than a bare non-null assertion) so this module can be
// imported by a test for `AthleteTree` above without needing a `#root`
// element in the document — index.html always has one, so production
// behavior is unchanged.
const rootEl = document.getElementById('root');
if (rootEl) {
  ReactDOM.createRoot(rootEl).render(
    <React.StrictMode>
      <ErrorBoundary>
        <AuthProvider>
          <AthleteTree>
            <App />
          </AthleteTree>
        </AuthProvider>
      </ErrorBoundary>
    </React.StrictMode>,
  );
}
